import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from "react-router-dom";
import { toast, ToastContainer } from 'react-toastify';
import "react-toastify/dist/ReactToastify.css";
import "./toast.css"
import axios from 'axios';
import { Typography, 
          Box,
          Dialog, 
          Table, 
          TableContainer, 
          TableCell, 
          TableRow, 
          TableHead,
          TableBody, 
          Button, Grid2,
          Radio,
          TablePagination,
          useMediaQuery,} from '@mui/material';
import SubmissionDetails from './components/submissionDialogs/submissionDetails';
import AcceptSubmission from './components/submissionDialogs/acceptSubmission';
import RejectSubmission from './components/submissionDialogs/rejectSubmission';
import DeleteSubmission from './components/submissionDialogs/deleteSubmission';

const columns = [
  { id: 'radioButtons', minWidth: 10},
  { id: 'organization', label: 'Organization', minWidth: 125},
  { id: 'user', label: 'User', minWidth: 165},
  { id: 'subType', label: 'Submission Type', minWidth: 160},
  { id: 'fileName', label: 'File Name', minWidth: 280},
  { id: 'subDate', label: 'Submission Date', minWidth: 140},
  { id: 'lastUpdated', label: 'Last Updated', minWidth: 140},
  { id: 'malScan', label: 'Scan', minWidth: 110},
  { id: 'validation', label: 'Validation', minWidth: 110},
  { id: 'status', label: 'Submission Status', minWidth: 127, paddingRight: 20}
];

const Admin = ({isInternal, toastOptions}) => {
  const serverHost = process.env.REACT_APP_BACKEND_SERVER_HOST || 'https://localhost:3001';
  const itemsPerPage = 10;
  const location = useLocation();
  const [submissions, setSubmissions] = useState([]);
  const [selectedValue, setSelectedValue] = useState('');
  const [page, setPage] = useState(0);
  const [pageToken, setPageToken] = useState([]);
  const [newPageToken, setNewPageToken] = useState("");
  const [totalCount, setTotalCount] = useState(0);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [showAcceptDialog, setShowAcceptDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [viewDetails, setViewDetails] = useState({});
  const [rowsPerPage, setRowsPerPage] = useState(itemsPerPage);
  const [updateTimestamp, setUpdateTimestamp] = useState(Date.now());

  const fullScreen = useMediaQuery('(max-width:600px)');

  const handleDetailsDialogOpen = () => setShowDetailsDialog(true);
  const handleDetailsDialogClose = () => setShowDetailsDialog(false); 
  const handleAcceptDialogOpen = () => setShowAcceptDialog(true);
  const handleAcceptDialogClose = () => setShowAcceptDialog(false);
  const handleRejectDialogOpen = () => setShowRejectDialog(true);
  const handleRejectDialogClose = () => setShowRejectDialog(false);
  const handleDeleteDialogOpen = () => setShowDeleteDialog(true);
  const handleDeleteDialogClose = () => setShowDeleteDialog(false);

  const fetchSubmissions = useCallback(async () => {
    try {
      const response = await axios.get(`${serverHost}/submissions?pageSize=${rowsPerPage}&pageToken=${pageToken.length > 0 ? pageToken.slice(-1)[0] : ""}`, { withCredentials: true });
      if (response.status === 200) {
          const {
            submissions,
            pageSize,
            nextPageToken,
            totalCount
          } = response.data;
          // no need to sort, comes back in reverse chronological order
          setSubmissions(submissions);
          setRowsPerPage(pageSize);
          setNewPageToken(nextPageToken);
          setTotalCount(totalCount);
      }
    } catch (error) {
      toast.error('Error fetching submissions:', error.response ? error.response.data : error.message, toastOptions);
    }
  }, [serverHost, pageToken, rowsPerPage, toastOptions]);
  
  const handlePageChange = (event, newPage) => {
    if(newPage > page) {
      setPage(newPage);
      setPageToken(prevTokens => [...prevTokens, newPageToken]);
    } else {
      setPage(newPage);
      setPageToken(prevItems => {
        const newTokenArr = [...prevItems];
        newTokenArr.pop();
        return newTokenArr;
      });
    }
  }

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
    setPageToken(pageToken.filter(() => false));
  }

  const handleActionSubmit = (action) => {
    if(action === 'approved') {
      toast.success("Submission successfully approved", toastOptions);
      setUpdateTimestamp(Date.now()); 
    } else if (action === 'rejected') {
      toast.success("Submission successfully rejected", toastOptions);
      setUpdateTimestamp(Date.now());
    } else if (action === 'deleted') {
      toast.success("Submission successfully deleted", toastOptions);
      setUpdateTimestamp(Date.now());
    } else {
      toast.error("Submission action error.  Please try again.", toastOptions);
    }
  }

  const handleRadioChange = (event, submission) => {
    setSelectedValue(submission.upload_key);
    setViewDetails(submission);
  }

  const changeTrack = {
    'dataset': 'Dataset',
    'track1':  'Document Validation',
    'track2':  'Match to ID',
    'track3':  'Passive PAD',
    'matching-system': 'Matching System'
  }

  const changeTypeName = (typeName) => {
   return changeTrack[typeName] || typeName; 
  }

  useEffect(() => {
    if(location.state?.formSubmitted) {
        fetchSubmissions();
        toast.success("Files uploaded successfully.  Submission complete.", toastOptions);
    } else {
      fetchSubmissions();
    }
  }, [fetchSubmissions, updateTimestamp, location, toastOptions]);

  return (
    <Box className='container-box'>
      <Box className='page-box' >
          <Typography color="fontTheme" 
                      className='page-header'>MyConsole</Typography>
          {!isInternal ?
          <Typography className='page-description'>
            As an admin, you can manage all supported features of the MyConsole interface. This includes user management, 
            dataset/docker submission approval workflow with detailed submission history. Your MyConsole main panel presents a summary table of all submissions. 
            To view details or update the status of a specific submission, select the radio button and proceed by selecting an available action button. 
            To ensure you are working with the most up-to-date information, select the 'Refresh Table' button.
          </Typography>
          :
          <Typography className='page-description'>
            As an Internal user, you can view a summary table and detailed history of all dataset and docker container submissions. 
            To view details or update the status of a specific submission, select the radio button and proceed by selecting an available action button. 
            To ensure you are working with the most up-to-date information, select the 'Refresh Table' button.
          </Typography>
          }
      </Box>
      <Box className='below-content'>
        <Grid2 container spacing={2} sx={{justifyContent: 'space-between'}}>
            <Grid2 item sm={12} md={6} lg={4} sx={{justifyContent: 'space-between'}}>
                <Typography color="fontTheme"
                            variant="h5" 
                            marginBottom="12px"
                            className='page-header'>
                    Submission Summary ({totalCount})
                </Typography>
            </Grid2>
            <Grid2 item sm={12} md={6} lg={8} className="useradmin-buttons">
                <Box className='table-buttons-div'>
                    <Button className="table-buttons"  variant="outlined" onClick={() => handleDetailsDialogOpen()} disabled={Object.keys(viewDetails).length === 0}>VIEW DETAILS</Button>
                </Box>
                <Box className='table-buttons-div'>
                    <Button className="table-buttons" disabled={!selectedValue || isInternal || viewDetails.approval_status === 'ACCEPTED'}  variant="outlined" onClick={() => handleAcceptDialogOpen()} >ACCEPT</Button>
                </Box>
                <Box className='table-buttons-div'>
                    <Button className="table-buttons" disabled={!selectedValue || isInternal || viewDetails.approval_status === 'REJECTED'}  variant="outlined" onClick={() => handleRejectDialogOpen()} >REJECT</Button>
                </Box>
                <Box className='table-buttons-div'>
                    <Button className="table-buttons" disabled={!selectedValue || isInternal || viewDetails.approval_status === 'DELETED'}  variant="outlined" onClick={() => handleDeleteDialogOpen()} >DELETE</Button>
                </Box>
                <Box className='table-buttons-div'>
                    <Button className="table-buttons"  component={Link} onClick={() => fetchSubmissions()} variant="outlined">REFRESH TABLE</Button>
                </Box>
            </Grid2>
        </Grid2>
        <TableContainer>
          <Table sx={{width: '100%', border: 1, borderColor: '#E0E0E0'}}>
            <TableHead sx={{backgroundColor:'#F2F0F0'}}>
              <TableRow sx={{'.MuiTableCell-root': {fontWeight: 'bold'}}}>
                {columns.map((column) => (
                    <TableCell 
                      className='table-text'
                      key={column.id} 
                      style={{minWidth: column.minWidth, paddingTop: '16px', paddingBottom: '16px', paddingRight: column.paddingRight}}>
                        {column.label} 
                    </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
            {submissions.map((submission, index) => (
                <TableRow 
                  hover
                  key={index}
                  onClick={(event) => handleRadioChange(event, submission)}
                  role="radio"
                >
                  <TableCell className='table-text' sx={{width: '0px'}}>
                    <Radio checked={selectedValue === submission.upload_key} color='fontTheme' sx={{padding: '13px', marginLeft: '13px', marginRight: '25px'}}/>
                  </TableCell>
                  <TableCell className='table-text'>{submission.company_name}</TableCell>
                  <TableCell className='table-text'>{submission.company_email}</TableCell>
                  <TableCell className='table-text'>{submission.upload_type ? changeTypeName(submission.upload_type) : ''}</TableCell>
                  <TableCell className='table-text'>{submission.upload_key ? submission.upload_key.split('/').reverse()[0] : ''}</TableCell>
                  <TableCell className='table-text' sx={{whiteSpace: 'pre-line'}}>
                    {submission.upload_timestamp ? new Date(submission.upload_timestamp).toLocaleString().split(",").join("\n") : ''}
                  </TableCell>
                  <TableCell className='table-text' sx={{whiteSpace: 'pre-line'}}>
                    {submission.last_updated ? new Date(submission.last_updated).toLocaleString().split(",").join("\n") : ''}
                  </TableCell>
                  <TableCell className='table-text'>
                    {submission.scan_status ? submission.scan_status.charAt(0) + submission.scan_status.slice(1).toLowerCase() : ''}
                  </TableCell>
                  <TableCell className='table-text'>
                    {submission.validation_status ? submission.validation_status.charAt(0) + submission.validation_status.slice(1).toLowerCase() : ''}
                  </TableCell>
                  <TableCell className='table-text' sx={{paddingRight: '5px'}}>
                    {submission.approval_status ? submission.approval_status.charAt(0) + submission.approval_status.slice(1).toLowerCase() : 'Submission in Review'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination  component="div"
                          count={totalCount}
                          page={page}
                          onPageChange={handlePageChange}
                          rowsPerPage={rowsPerPage}
                          onRowsPerPageChange={handleRowsPerPageChange}
                          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Box>
      <ToastContainer autoClose={5000} />
    
      {/* SUBMISSION DETAILS DIALOG */}
      <Dialog open={showDetailsDialog}
              onClose={handleDetailsDialogClose}
              aria-labelledby='alert-dialog-title'
              aria-describedby='alert-dialog-description'
              fullScreen={fullScreen}
      >
        <SubmissionDetails 
          organization={viewDetails?.company_name} 
          subType={viewDetails?.upload_type} 
          fileName={viewDetails?.upload_key} 
          email={viewDetails?.company_email}
          onCloseSub={handleDetailsDialogClose}
        />
      </Dialog>

      {/* ACCEPT SUBMISSION DIALOG */}
      <Dialog open={showAcceptDialog}
              onClose={handleAcceptDialogClose}
              aria-labelledby='alert-dialog-title'
              aria-describedby='alert-dialog-description'
              fullScreen={fullScreen}
      >
        <AcceptSubmission 
          organization={viewDetails?.company_name} 
          subType={viewDetails?.upload_type} 
          fileName={viewDetails?.upload_key}
          email={viewDetails?.company_email}
          onCloseAccept={handleAcceptDialogClose}
          actionSubmitted={handleActionSubmit}
        />
      </Dialog>

      {/* REJECT SUBMISSION DIALOG */}
      <Dialog open={showRejectDialog}
              onClose={handleRejectDialogClose}
              aria-labelledby='alert-dialog-title'
              aria-describedby='alert-dialog-description'
              fullScreen={fullScreen}
      >
        <RejectSubmission
          organization={viewDetails?.company_name} 
          subType={viewDetails?.upload_type} 
          fileName={viewDetails?.upload_key}
          email={viewDetails?.company_email}
          onCloseReject={handleRejectDialogClose}
          actionSubmitted={handleActionSubmit}
        />
      </Dialog>

      {/* DELETE SUBMISSION DIALOG */}
      <Dialog open={showDeleteDialog}
              onClose={handleDeleteDialogClose}
              aria-labelledby='alert-dialog-title'
              aria-describedby='alert-dialog-description'
              fullScreen={fullScreen}
      >
        <DeleteSubmission
          organization={viewDetails?.company_name} 
          subType={viewDetails?.upload_type} 
          fileName={viewDetails?.upload_key}
          email={viewDetails?.company_email}
          onCloseDelete={handleDeleteDialogClose}
          actionSubmitted={handleActionSubmit}
        />
      </Dialog>
    </Box>
  );
}

export default Admin;
